import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { canEditScript, silentLines, silentLinesMessage, sourceUrl } from '@reelmachine/core';
import { useCurrentOrg, useReel, useReelAction } from '../../api/hooks';
import { ErrorView, HistoryButton, Loading, Screen } from '../../ui/Screen';
import { Stepper } from '../../ui/Stepper';
import { useToast } from '../../ui/Toast';
import { DESKTOP, useMediaQuery } from '../../ui/useMediaQuery';
import { brandColor } from '../ReelList';
import { ChatTab } from './ChatTab';
import { GeneratingView } from './GeneratingView';
import { ScriptTab } from './ScriptTab';
import { StoryboardTab } from './StoryboardTab';
import { useScriptEditor } from './useScriptEditor';

type Tab = 'script' | 'storyboard' | 'chat';
const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'script', label: 'Script' },
  { id: 'storyboard', label: 'Storyboard' },
  { id: 'chat', label: 'Chat' },
];
export const HIGHLIGHT_MS = 6000;

export function ScriptScreen() {
  const { reelId = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const q = useReel(reelId);
  const { org } = useCurrentOrg();
  const editor = useScriptEditor(reelId, q.data?.script);
  const approve = useReelAction(reelId, 'approve');
  const regenerate = useReelAction(reelId, 'regenerate');
  const cancel = useReelAction(reelId, 'cancelJob');
  const [tab, setTab] = useState<Tab>('script');
  const desktop = useMediaQuery(DESKTOP);
  // Desktop: the script stays on the left, the side panel shows storyboard or chat.
  const [side, setSide] = useState<Exclude<Tab, 'script'>>('storyboard');
  const [highlight, setHighlight] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    if (highlight.size === 0) return;
    const t = setTimeout(() => setHighlight(new Set()), HIGHLIGHT_MS);
    return () => clearTimeout(t);
  }, [highlight]);

  const onChanged = useCallback((changed: string[]) => {
    setHighlight(new Set(changed));
    setTab('script');
  }, []);

  // Bring the first changed field into view.
  useEffect(() => {
    if (highlight.size === 0) return;
    const el = document.querySelector('.changed');
    if (el && 'scrollIntoView' in el) (el as HTMLElement).scrollIntoView?.({ block: 'center' });
  }, [highlight, tab]);

  if (q.isError) return <ErrorView back="/" error={q.error} />;
  if (q.isPending) return <Loading />;
  const { reel, script } = q.data;
  const job = reel.job?.kind === 'script' ? reel.job : undefined;

  if (job?.status === 'failed') {
    return (
      <Screen
        title="Writing script"
        back="/"
        bar={
          <button type="button" className="btn" disabled={regenerate.isPending} onClick={() => regenerate.mutate()}>
            Try again
          </button>
        }
      >
        <div className="card warnc" role="alert">
          <h3>The script could not be written</h3>
          <p>{job.error ?? 'Something went wrong.'}</p>
        </div>
      </Screen>
    );
  }
  if (job?.status === 'running') {
    return (
      <Screen
        title="Writing script"
        back="/"
        bar={
          <button type="button" className="btn sec" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
            {cancel.isPending ? 'Cancelling…' : 'Cancel'}
          </button>
        }
      >
        <GeneratingView job={job} />
      </Screen>
    );
  }
  if (!script) {
    // cancelled before the first script was written
    return (
      <Screen
        title={reel.title}
        back="/"
        bar={
          <button type="button" className="btn" disabled={regenerate.isPending} onClick={() => regenerate.mutate()}>
            Write script
          </button>
        }
      >
        <p className="muted">No script yet.</p>
      </Screen>
    );
  }

  const readOnly = !canEditScript(reel);
  // live editor values: an emptied line blocks approving at once, not only after the save
  const silent = silentLines({ lines: script.lines.map((l) => ({ ...l, text: editor.value(l.id, 'text') })) });
  const doApprove = async () => {
    try {
      await editor.flush();
      await approve.mutateAsync();
      navigate(`/reels/${reelId}/record`);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not approve the script.');
    }
  };
  const switchTab = (t: Tab) => {
    void editor.flush();
    setTab(t);
  };
  const switchSide = (t: Exclude<Tab, 'script'>) => {
    void editor.flush();
    setSide(t);
  };

  const storyboard = (
    <StoryboardTab script={script} editor={editor} brand={brandColor(org)} highlight={highlight} readOnly={readOnly} />
  );
  const chat = <ChatTab reelId={reelId} readOnly={readOnly} beforeSend={editor.flush} onChanged={onChanged} />;

  return (
    <Screen
      title={reel.title}
      titleHref={sourceUrl(reel.source)}
      width={desktop ? 'wide' : 'narrow'}
      back="/"
      right={<HistoryButton reelId={reelId} />}
      bar={
        readOnly ? undefined : (
          <button
            type="button"
            className="btn"
            disabled={approve.isPending || silent.length > 0}
            onClick={() => void doApprove()}
          >
            Approve script
          </button>
        )
      }
      barHint={!readOnly && silent.length ? silentLinesMessage(silent) : undefined}
    >
      <Stepper reel={reel} current="script" />
      {readOnly && <p className="muted small">This reel is posted. The script is read-only.</p>}
      {desktop ? (
        <div className="split">
          <section aria-label="Script" className="split-main">
            <ScriptTab script={script} editor={editor} highlight={highlight} readOnly={readOnly} />
          </section>
          <aside className="split-side">
            <div className="seg" role="tablist" aria-label="Side panel">
              {TABS.filter((t) => t.id !== 'script').map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  id={`tab-${t.id}`}
                  aria-selected={side === t.id}
                  aria-controls={`panel-${t.id}`}
                  onClick={() => switchSide(t.id as Exclude<Tab, 'script'>)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div role="tabpanel" id={`panel-${side}`} aria-labelledby={`tab-${side}`} className="stack">
              {side === 'storyboard' ? storyboard : chat}
            </div>
          </aside>
        </div>
      ) : (
        <>
          <div className="seg" role="tablist" aria-label="Script views">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                id={`tab-${t.id}`}
                aria-selected={tab === t.id}
                aria-controls={`panel-${t.id}`}
                onClick={() => switchTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`} className="stack">
            {tab === 'script' && (
              <ScriptTab script={script} editor={editor} highlight={highlight} readOnly={readOnly} />
            )}
            {tab === 'storyboard' && storyboard}
            {tab === 'chat' && chat}
          </div>
        </>
      )}
    </Screen>
  );
}
